import type { GenericGraphProps } from "@similarweb/pro-logic-components";

import { CHART_COLORS } from "app/constants/ChartColors";
import type { AiAgentEmailAutocompleteCompany } from "app/pages/sales-intelligence/modules/ai-agent/types/email";
import type { Competitor } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";

import type { ChartObject, ChartTrendSeries, CompareChartGraphDataItem } from "./types";

const SCALE_RATIO_THRESHOLD = 40;

const createChartStreamService = () => {
    const isChartObject = (obj: unknown): obj is ChartObject => {
        const chartObj = obj as Partial<ChartObject>;
        if (
            typeof obj !== "object" ||
            obj === null ||
            typeof chartObj.chart?.type !== "string" ||
            !Array.isArray(chartObj.series) ||
            typeof chartObj.title?.text !== "string" ||
            !Array.isArray(chartObj.xAxis?.categories)
        ) {
            return false;
        }

        const seriesValid = chartObj.series.every(
            (s) =>
                typeof s.name === "string" &&
                Array.isArray(s.data) &&
                s.data.every((d: { y: number; [key: string]: unknown }) => typeof d.y === "number" || d.y === null),
        );

        return seriesValid;
    };

    const normalize = (str: string) =>
        str
            .toLowerCase()
            .replace(/\s*&\s*/g, " & ") // normalize spacing around &
            .replace(/&/g, "and") // treat & as 'and'
            .replace(/[^a-z0-9\s]/g, "") // remove all other non-alphanumeric chars
            .replace(/\s+/g, " ") // collapse multiple spaces
            .trim();

    const matchesDomainMeta = (value: string | undefined, meta?: Competitor): boolean => {
        if (!value || !meta) {
            return false;
        }

        const { domain, companyName } = meta;
        const normalizedValue = normalize(value);
        const normalizedDomain = domain ? normalize(domain) : "";
        const normalizedCompanyName = companyName ? normalize(companyName) : "";

        return (
            normalizedValue === normalizedDomain ||
            normalizedValue === normalizedCompanyName ||
            normalizedCompanyName.includes(normalizedValue) ||
            normalizedDomain.includes(normalizedValue)
        );
    };

    const findDomainMeta = (domains: Competitor[] | undefined, value: string | undefined): Competitor | undefined => {
        return domains?.find((d) => matchesDomainMeta(value, d));
    };

    const isValidProspectIndex = (index: number | undefined, length: number): boolean =>
        typeof index === "number" && index >= 0 && index < length;

    const isCompareChart = (chart: ChartObject): boolean =>
        chart.series.length === 1 &&
        chart?.xAxis?.categories?.length > 0 &&
        !!chart.xargs?.domains &&
        chart.xargs?.domains.length > 0 &&
        chart.xAxis.categories.some((cat) => chart.xargs?.domains?.some((d) => matchesDomainMeta(cat, d)));

    const transformDataToCompareChart = (
        chart: ChartObject,
        selectedCompany: AiAgentEmailAutocompleteCompany | null,
    ): CompareChartGraphDataItem[] => {
        const series = chart?.series?.[0];
        const rawSeriesData = series?.data || [];
        const data = rawSeriesData
            .map((c, i) => {
                const xAxisValue = chart?.xAxis?.categories?.[i];
                const xAxisDomainData = findDomainMeta(chart?.xargs?.domains, xAxisValue);
                const prospectIndex = series?.prospectIndex;
                const hasValidProspectIndex = isValidProspectIndex(prospectIndex, rawSeriesData.length);
                const isHighlighted = hasValidProspectIndex
                    ? i === prospectIndex
                    : selectedCompany?.domain === xAxisDomainData?.domain;

                return {
                    name: xAxisDomainData?.companyName ?? xAxisValue,
                    favicon: xAxisDomainData?.favicon,
                    value: Math.round(c.y * 100) / 100,
                    isHighlighted,
                };
            })
            .filter((item) => !!item.value)
            .filter(Boolean) as CompareChartGraphDataItem[];
        return data.sort((a, b) => b.value - a.value);
    };

    const toNums = (s: { data: Array<{ y: number | null | undefined }> }) => s.data.map((p) => Number(p?.y) || 0);

    const seriesMaxAbs = (arr: number[]) => (arr.length ? Math.max(...arr.map((v) => Math.abs(v))) : 0);

    const getScaleRatio = (maxes: number[]) => {
        const nonZeroMaxes = maxes.filter((v) => v > 0);
        if (nonZeroMaxes.length < 2) {
            return 1;
        }
        return Math.max(...nonZeroMaxes) / Math.min(...nonZeroMaxes);
    };

    const hasMixedFormat = (chart: ChartObject): boolean => {
        const formats = Array.from(new Set((chart.series ?? []).map((s) => s?.dataFormat ?? "number")));
        const isMixedByFormat = formats.length > 1;

        const maxes = (chart.series ?? []).map((s) => seriesMaxAbs(toNums(s)));
        const isMixedByScale = getScaleRatio(maxes) >= SCALE_RATIO_THRESHOLD;

        return isMixedByFormat || isMixedByScale;
    };

    const transformDataToChart = (
        chart: ChartObject,
        selectedCompany: AiAgentEmailAutocompleteCompany | null,
    ): GenericGraphProps | ChartTrendSeries[] => {
        const isMixed = hasMixedFormat(chart);
        const isBarLike = chart.chart.type === "bar" || chart.chart.type === "column";
        const maxes = (chart.series ?? []).map((s) => seriesMaxAbs(toNums(s)));
        const primaryIndex = isMixed ? maxes.indexOf(Math.max(...maxes)) : 0;

        return chart?.series?.map((item, seriesIndex) => {
            const seriesData: number[] = item.data.map((point) => point.y || 0);
            const xAxisDomainData = chart?.xargs?.domains?.find((d) => d.domain === item.domain);
            const isSelectedCompany = selectedCompany?.domain === item.domain;
            const isPrimary = seriesIndex === primaryIndex;

            const base = {
                color: CHART_COLORS.main[seriesIndex],
                data: seriesData,
                id: `series ${seriesIndex}`,
                name: isSelectedCompany ? selectedCompany?.companyName : xAxisDomainData?.companyName || item.name,
                visible: true,
                type: isBarLike ? chartTypes.COLUMN : chartTypes.LINE,
            } as ChartTrendSeries;

            if (isMixed && isBarLike) {
                return {
                    ...base,
                    yAxis: isPrimary ? 0 : 1,
                    stacking: undefined,
                };
            }
            return base;
        });
    };

    const getChartTitle = (chart?: Record<string, unknown> | null): string => {
        let chartTitle = "";
        if (isChartObject(chart)) {
            chartTitle = chart.title?.text;
        }
        return chartTitle;
    };

    const getChartCompetitors = (chart?: Record<string, unknown> | null): Competitor[] => {
        if (!isChartObject(chart) || !chart.xargs?.domains) {
            return [];
        }

        const competitors = chart.xargs.domains.slice(1); // skip prospect

        const categories = chart.xAxis?.categories ?? [];
        const seriesDomains = (chart.series ?? [])
            .map((s) => s.domain)
            .filter((d): d is string => typeof d === "string");

        const appearsInXAxis = (c: Competitor) => categories.some((cat) => matchesDomainMeta(cat, c));
        const appearsInSeriesDomain = (c: Competitor) => seriesDomains.some((sd) => matchesDomainMeta(sd, c));

        const matched = competitors.filter((c) => appearsInXAxis(c) || appearsInSeriesDomain(c));

        if (isCompareChart(chart)) {
            const series = chart.series?.[0];
            return matched.filter((c) => {
                const idx = categories.findIndex((cat) => matchesDomainMeta(cat, c));
                if (idx < 0) {
                    return false;
                }
                const y = series.data[idx]?.y;
                return typeof y === "number" && y !== 0;
            });
        }
        return matched;
    };

    const getChartKey = (chart?: Record<string, unknown> | null): string => {
        if (!isChartObject(chart)) {
            return "no-chart";
        }
        const title = chart.title?.text || "untitled";
        const seriesCount = chart.series.length;
        const categories = (chart.xAxis?.categories || []).map((c) => normalize(String(c))).join(",");
        const country = chart.xargs.country || "no-country";
        const date = chart.xargs?.date || "no-date";
        return [title, seriesCount, country, date, categories].filter(Boolean).join("|");
    };

    return {
        isChartObject,
        isCompareChart,
        hasMixedFormat,
        transformDataToChart,
        transformDataToCompareChart,
        getChartTitle,
        getChartCompetitors,
        getChartKey,
    };
};

const ChartStreamService = createChartStreamService();

export default ChartStreamService;
